import { useState } from 'react'
import { Check, Film, ImageIcon } from 'lucide-react'
import { apiUrl } from '../../../api/client'
import type { VisualAsset, VisualGroup } from '../../../api/visuals'
import { Button, EmptyState, Modal, ModalActions, Pill } from '../../../components/ui'
import styles from './VisualGroupAssetPicker.module.css'

type VisualGroupAssetPickerProps = {
  assets: VisualAsset[]
  group: VisualGroup
  onClose: () => void
  onSave: (assetIds: string[]) => Promise<void>
}

export const VisualGroupAssetPicker = (props: VisualGroupAssetPickerProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    props.assets
      .filter((asset) => asset.groups.some((group) => group.id === props.group.id))
      .map((asset) => asset.id)
  )
  const [saving, setSaving] = useState(false)

  const toggleAsset = (assetId: string): void => {
    setSelectedIds((current) =>
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId]
    )
  }

  const cssClass = (...classNames: Array<string | false | null | undefined>): string =>
    classNames.filter(Boolean).join(' ')

  return (
    <Modal
      ariaLabelledBy="group-assets-title"
      onClose={props.onClose}
      panelClassName={cssClass(styles.panel)}
    >
        <div className={styles.heading}>
          <div>
            <h2 id="group-assets-title">Manage assets - {props.group.name}</h2>
            <p>Select images and videos included in this visual group.</p>
          </div>
          <Pill strong>{selectedIds.length} selected</Pill>
        </div>
        <div className={styles.grid}>
          {props.assets.map((asset) => {
            const selected = selectedIds.includes(asset.id)

            return (
              <button
                className={cssClass(styles.item, selected && styles.selected)}
                key={asset.id}
                onClick={() => toggleAsset(asset.id)}
                type="button"
              >
                {asset.thumbnail_url ? (
                  <img alt="" src={`${apiUrl}${asset.thumbnail_url}`} />
                ) : asset.asset_type === 'image' ? (
                  <ImageIcon size={26} />
                ) : (
                  <Film size={26} />
                )}
                <span className={styles.check}><Check size={14} /></span>
                <span className={styles.title}>{asset.title}</span>
              </button>
            )
          })}
        </div>
        {props.assets.length === 0 && <EmptyState message="No visual assets available." />}
        <ModalActions>
          <Button onClick={props.onClose} variant="secondary">Cancel</Button>
          <Button
            disabled={saving}
            onClick={() => {
              setSaving(true)
              void props.onSave(selectedIds).finally(() => setSaving(false))
            }}
            variant="primary"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ModalActions>
    </Modal>
  )
}
